import React from 'react';
import Navbar from "../Navbar/Nabvar";
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Container,
    Card,
    CardContent,
    CardMedia,
    Button,
    styled,
    useTheme
} from '@mui/material';
import Footer from '../Footer/Footer';
import QuoteBox from '../Quote/QuoteBox';
import {getBaseUrl} from "../Config/BaseUrl";
import { mock_methods } from '../KnowHow/mock_methods';

// Styled components
const StyledContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    paddingTop: '80px',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
        linear-gradient(to bottom right, 
            rgba(240, 245, 255, 0.9),
            rgba(230, 240, 255, 0.9)
        )
    `,
        zIndex: 0,
    }
}));

const ContentWrapper = styled(Box)({
    position: 'relative',
    zIndex: 1,
    width: '100%',
});

const ContentSection = styled(Box)(({ theme }) => ({
    width: '100%',
    margin: 0,
    backgroundColor: '#fff',
    borderTop: '0.5px solid #2514b8',
    borderBottom: '0.5px solid #2514b8',
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)',
    marginTop: theme.spacing(18.75), // 150px
    marginBottom: theme.spacing(18.75),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    margin: '20px auto',
    padding: '15px 30px',
    backgroundColor: '#ed028c',
    color: '#fff',
    fontSize: '1.5em',
    width: '250px',
    '&:hover': {
        backgroundColor: '#ed028c',
        opacity: 0.8,
    },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    marginTop: theme.spacing(2.5),
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'center',
    },
    '& img': {
        maxWidth: '700px',
        maxHeight: '700px',
        width: '100%',
        margin: '0 10px',
        [theme.breakpoints.down('lg')]: {
            maxWidth: '500px',
            maxHeight: '500px',
        },
        [theme.breakpoints.down('md')]: {
            maxWidth: '90%',
            margin: '10px 0',
        },
    },
}));

const CardGrid = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',
    padding: '0 20px',
    gap: '20px',
    '& .MuiCard-root': {
        width: 300,
        height: 250,
        transition: 'transform 0.2s',
        '&:hover': {
            transform: 'scale(1.03)',
        },
        [theme.breakpoints.down('xl')]: {
            width: 280,
        },
        [theme.breakpoints.down('lg')]: {
            width: 260,
        },
        [theme.breakpoints.down('md')]: {
            width: '45%',
            maxWidth: 240,
        },
    },
    [theme.breakpoints.down('sm')]: {
        gap: '15px',
        padding: '0 10px',
        '& .MuiCard-root': {
            width: '100%',
            maxWidth: 320,
            margin: '0 auto',
        },
    },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    fontSize: '1.7em',
    color: '#2514b8',
    marginRight: 100,
    marginLeft: 100,
    mb: 3,
    [theme.breakpoints.down('md')]: {
        fontSize: '1.5em',
        marginRight: 50,
        marginLeft: 50,
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.3em',
        width: '90%',
        margin: theme.spacing(2),
    },
}));

const Main: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const primaryBlue = '#1F1B9E';

    const handleTransformClick = () => {
        navigate('/Transformation');
    };

    const handleTrendsClick = () => {
        navigate('/Trends');
    };

    return (
        <StyledContainer>
            <ContentWrapper>
                <Navbar />

                <Box>
                    <img
                        src="old/bmLogoNewNew.png"
                        alt="Logo"
                        style={{
                            maxHeight: 600,
                            width: 'auto',
                            maxWidth: '100%',
                            display: 'block',
                            margin: '0 auto'
                        }}
                    />

                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryBlue,
                            maxWidth: '1800px',
                            mx: 'auto',
                            px: { xs: 2, sm: 3, md: 4 },
                            fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.5rem' },
                            mb: 3
                        }}
                    >
                        Willkommen bei BM-MAPS
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryBlue,
                            maxWidth: '1600px',
                            mx: 'auto',
                            px: 2,
                            fontSize: '1.5rem',
                            mb: 3 // Abstand nach unten
                        }}
                    >
                        Genau wie eine gut geplante Bergtour bedarf es auch bei der Veränderung des Geschäftsmodells einer gewissen Vorbereitung und
                        Vorinformation: Von wo starte ich, wo soll es hingehen, welche Routen führen zum Ziel und was benötige ich dafür?
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryBlue,
                            maxWidth: '1600px',
                            mx: 'auto',
                            px: 2,
                            fontSize: '1.5rem',
                            mb: 3 // Abstand nach unten
                        }}
                    >
                        BM-MAPS hilft Ihnen mit breit gefächerten Ansätzen, die perfekte Route für Ihre Geschäftsmodellveränderung zu finden und die richtigen
                        Werkzeuge für Ihren erfolgreichen, schnellen und sicheren Aufstieg einzupacken.
                    </Typography>
                </Box>

                {/* Hiking image section */}
                {/*<Box*/}
                {/*    sx={{*/}
                {/*        width: '100%',*/}
                {/*        position: 'relative',*/}
                {/*        my: 4,*/}
                {/*        '&::before': {*/}
                {/*            content: '""',*/}
                {/*            position: 'absolute',*/}
                {/*            top: 0,*/}
                {/*            left: 0,*/}
                {/*            right: 0,*/}
                {/*            height: '1px',*/}
                {/*            backgroundColor: primaryBlue,*/}
                {/*        },*/}
                {/*        '&::after': {*/}
                {/*            content: '""',*/}
                {/*            position: 'absolute',*/}
                {/*            bottom: 0,*/}
                {/*            left: 0,*/}
                {/*            right: 0,*/}
                {/*            height: '1px',*/}
                {/*            backgroundColor: primaryBlue,*/}
                {/*        }*/}
                {/*    }}*/}
                {/*>*/}
                {/*    <Box*/}
                {/*        sx={{*/}
                {/*            width: '100%',*/}
                {/*            height: { xs: '400px', sm: '600px', md: '800px' },*/}
                {/*            backgroundImage: 'url("/old/wandern_kontakt.png")',*/}
                {/*            backgroundSize: 'cover',*/}
                {/*            backgroundPosition: 'center',*/}
                {/*        }}*/}
                {/*    />*/}
                {/*</Box>*/}


                <ContentSection>
                    <Box component="img"
                         src="/old/bmTransform_new.png"
                         sx={{
                             width: { xs: '90%', sm: '350px', md: '450px' },
                             maxWidth: '100%'
                         }}
                    />

                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryBlue,
                            maxWidth: '1000px',
                            mx: 'auto',
                            px: 2,
                            fontSize: '1.5rem',
                            mb: 3 // Abstand nach unten
                        }}
                    >
                        BM-Transform ist unser smarter Onlinekonfigurator, der Ihnen hilft, Ihr bestehendes Geschäftsmodell einfach zu finden und ein neues Geschäftsmodell zu suchen.
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryBlue,
                            maxWidth: '1000px',
                            mx: 'auto',
                            px: 2,
                            fontSize: '1.5rem',
                            mb: 3 // Abstand nach unten
                        }}
                    >
                        Sie erhalten automatisch Ihre individuellen Geschäftsmodellanalysen als Download.
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryBlue,
                            maxWidth: '1000px',
                            mx: 'auto',
                            px: 2,
                            fontSize: '1.5rem',
                            mb: 3 // Abstand nach unten
                        }}
                    >
                        Bei Bedarf stehen Ihnen unsere Expert*innen und Partner*innen für eine weiterführende Beratung gerne online oder vor Ort zur Verfügung.
                    </Typography>

                    <ImageContainer>
                        <img src="/imac-links.png" alt="Transform Image 1" />
                        <img src="/imac-rechts.png" alt="Transform Image 2" />
                    </ImageContainer>

                    <Button
                        variant="outlined"
                        sx={{
                            mt: 4,
                            color: primaryBlue,
                            borderColor: primaryBlue,
                            borderRadius: '24px',
                            padding: '8px 32px',
                            fontSize: '1.1rem',
                            '&:hover': {
                                borderColor: primaryBlue,
                                backgroundColor: 'rgba(31, 27, 158, 0.04)'
                            },
                            marginBottom: 10
                        }}
                        onClick={() => window.open("https://xxx.bm-maps.com/", "_blank")}
                    >
                        Geschäftsmodell<br />
                        transformieren
                    </Button>

                </ContentSection>

                {/* Hiking image section */}
                {/*<Box*/}
                {/*    sx={{*/}
                {/*        width: '100%',*/}
                {/*        position: 'relative',*/}
                {/*        my: 4,*/}
                {/*        '&::before': {*/}
                {/*            content: '""',*/}
                {/*            position: 'absolute',*/}
                {/*            top: 0,*/}
                {/*            left: 0,*/}
                {/*            right: 0,*/}
                {/*            height: '1px',*/}
                {/*            backgroundColor: primaryBlue,*/}
                {/*        },*/}
                {/*        '&::after': {*/}
                {/*            content: '""',*/}
                {/*            position: 'absolute',*/}
                {/*            bottom: 0,*/}
                {/*            left: 0,*/}
                {/*            right: 0,*/}
                {/*            height: '1px',*/}
                {/*            backgroundColor: primaryBlue,*/}
                {/*        }*/}
                {/*    }}*/}
                {/*>*/}
                {/*    <Box*/}
                {/*        sx={{*/}
                {/*            width: '100%',*/}
                {/*            height: { xs: '400px', sm: '600px', md: '800px' },*/}
                {/*            backgroundImage: 'url("/old/wandern_kontakt.png")',*/}
                {/*            backgroundSize: 'cover',*/}
                {/*            backgroundPosition: 'center',*/}
                {/*        }}*/}
                {/*    />*/}
                {/*</Box>*/}

                <ContentSection>
                    <Box component="img" src="/search.png" sx={{ width: 400 }} />

                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryBlue,
                            maxWidth: '1000px',
                            mx: 'auto',
                            px: { xs: 2, sm: 3, md: 4 },
                            fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.5rem' },
                            mb: 3
                        }}
                    >
                        Erfahren Sie mehr über die neuesten Einflüsse und Entwicklungen zur Weiterentwicklung Ihres Geschäftsmodells innerhalb von zwölf Megatrends.
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryBlue,
                            maxWidth: '1300px',
                            mx: 'auto',
                            px: { xs: 2, sm: 3, md: 4 },
                            fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.5rem' },
                            mb: 3,
                            '& br': {
                                display: { xs: 'none', md: 'block' }
                            }
                        }}
                    >
                        Lassen Sie sich inspirieren und sammeln Sie Ihre relevantesten Trends im Trendkorb -
                        bei Bedarf <br/> stehen Ihnen unsere Expert*innen und Partner*innen zur Weiterentwicklung Ihres Unternehmens zur Verfügung.

                    </Typography>

                    <CardGrid>
                        <Card sx={{
                            position: 'relative',
                            border: '2px solid #1976d2', // Blue border
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: "#1976d2"
                        }}>
                            <CardMedia
                                component="img"
                                sx={{
                                    height: { xs: '180px', sm: '200px', md: '250px' },
                                    objectFit: 'cover',
                                    backgroundColor: "#1976d2"
                                }}
                                image="Mobilitaet/Titel/titel.png"
                            />
                            <CardContent sx={{
                                position: 'absolute',
                                bottom: 0,
                                width: '100%',
                                background: '#1976d2', // Blue background
                                padding: { xs: '10px !important', sm: '16px !important' },
                            }}>
                                <Typography variant="h5" sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                                    marginLeft: "-10%"
                                }}>
                                    Mobilität
                                </Typography>
                            </CardContent>
                        </Card>

                        <Card sx={{
                            position: 'relative',
                            border: '2px solid #9c27b0', // Purple border
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: "#9c27b0"
                        }}>
                            <CardMedia
                                component="img"
                                sx={{
                                    height: { xs: '180px', sm: '200px', md: '250px' },
                                    objectFit: 'cover',
                                }}
                                image="Individualisierung/Titel/titel.png"
                                alt="Ökologische Geschäftsmodell-Trends"
                            />
                            <CardContent sx={{
                                position: 'absolute',
                                bottom: 0,
                                width: '100%',
                                background: '#9c27b0', // Purple background
                                padding: { xs: '10px !important', sm: '16px !important' },
                            }}>
                                <Typography variant="h5" sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem',marginLeft: "-10%" },
                                }}>
                                    Individualisierung
                                </Typography>
                            </CardContent>
                        </Card>

                        <Card sx={{
                            position: 'relative',
                            border: '2px solid #2e7d32', // Green border
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: "#2e7d32"
                        }}>
                            <CardMedia
                                component="img"
                                sx={{
                                    height: { xs: '180px', sm: '200px', md: '250px' },
                                    objectFit: 'cover',
                                }}
                                image="/Neo Oekologie/Titel/titel.png"
                                alt="Gesundheits-basierte Geschäftsmodell-Trends"
                            />
                            <CardContent sx={{
                                position: 'absolute',
                                bottom: 0,
                                width: '100%',
                                background: '#2e7d32', // Green background
                                padding: { xs: '10px !important', sm: '16px !important' },
                            }}>
                                <Typography variant="h5" sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem', marginLeft: "-10%" },
                                }}>
                                    Neo-Ökologie
                                </Typography>
                            </CardContent>
                        </Card>

                        <Card sx={{
                            position: 'relative',
                            border: '2px solid #ed6c02', // Orange border
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: "#ed6c02"
                        }}>
                            <CardMedia
                                component="img"
                                sx={{
                                    height: { xs: '180px', sm: '200px', md: '250px' },
                                    objectFit: 'cover',
                                }}
                                image="/Wissenskultur/Titel/titel.png"
                                alt="Faktor Mensch in modernen Geschäftsmodellen"
                            />
                            <CardContent sx={{
                                position: 'absolute',
                                bottom: 0,
                                width: '100%',
                                background: '#ed6c02', // Orange background
                                padding: { xs: '10px !important', sm: '16px !important' },
                            }}>
                                <Typography variant="h5" sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem', marginLeft: "-10%" },
                                }}>
                                    Wissenskultur
                                </Typography>
                            </CardContent>
                        </Card>
                    </CardGrid>

                    <Button
                        variant="outlined"
                        sx={{
                            mt: 4,
                            color: primaryBlue,
                            borderColor: primaryBlue,
                            borderRadius: '24px',
                            padding: '8px 32px',
                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                            width: { xs: '180px', sm: '200px', md: '200px' },
                            '&:hover': {
                                borderColor: primaryBlue,
                                backgroundColor: 'rgba(31, 27, 158, 0.04)'
                            },
                            marginBottom: 10
                        }}
                        onClick={() => {
                            window.location.href = `${getBaseUrl()}/Trends`
                        }}
                    >
                        Trends <br />
                        erkunden
                    </Button>
                </ContentSection>

                {/* Hiking image section */}
                {/*<Box*/}
                {/*    sx={{*/}
                {/*        width: '100%',*/}
                {/*        position: 'relative',*/}
                {/*        my: 4,*/}
                {/*        '&::before': {*/}
                {/*            content: '""',*/}
                {/*            position: 'absolute',*/}
                {/*            top: 0,*/}
                {/*            left: 0,*/}
                {/*            right: 0,*/}
                {/*            height: '1px',*/}
                {/*            backgroundColor: primaryBlue,*/}
                {/*        },*/}
                {/*        '&::after': {*/}
                {/*            content: '""',*/}
                {/*            position: 'absolute',*/}
                {/*            bottom: 0,*/}
                {/*            left: 0,*/}
                {/*            right: 0,*/}
                {/*            height: '1px',*/}
                {/*            backgroundColor: primaryBlue,*/}
                {/*        }*/}
                {/*    }}*/}
                {/*>*/}
                {/*    <Box*/}
                {/*        sx={{*/}
                {/*            width: '100%',*/}
                {/*            height: { xs: '400px', sm: '600px', md: '800px' },*/}
                {/*            backgroundImage: 'url("/old/wandern_kontakt.png")',*/}
                {/*            backgroundSize: 'cover',*/}
                {/*            backgroundPosition: 'center',*/}
                {/*        }}*/}
                {/*    />*/}
                {/*</Box>*/}

                <ContentSection>
                    <Box component="img" src="old/knowHow.png" sx={{ width: 400, marginTop: 5 }} />

                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryBlue,
                            maxWidth: '1500px',
                            mx: 'auto',
                            px: { xs: 2, sm: 3, md: 4 },
                            fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.5rem' },
                            mb: 3
                        }}
                    >
                        Verstehen, anwenden, weiterentwickeln – hier finden Sie fundiertes Wissen rund um Geschäftsmodelle, praxisbewährte Methoden und ausfüllbare Vorlagen. Ideal für alle, die ihre Ideen strukturiert in erfolgreiche Geschäftsmodelle überführen wollen.
                    </Typography>

                    <CardGrid>
                        {mock_methods.slice(0, 4).map((method) => (
                            <Card key={method.id} sx={{
                                position: 'relative',
                                border: '2px solid #1976d2',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                backgroundColor: "#1976d2"
                            }}>
                                <CardMedia
                                    component="img"
                                    sx={{
                                        height: { xs: '180px', sm: '200px', md: '250px' },
                                        objectFit: 'cover',
                                    }}
                                    image={method.image_kachel}
                                    alt={method.title}
                                />
                                <CardContent sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    width: '100%',
                                    background: '#1976d2',
                                    padding: { xs: '10px !important', sm: '16px !important' },
                                }}>
                                    <Typography variant="h6" sx={{
                                        color: 'white',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        marginLeft: '-35px',
                                        fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
                                    }}>
                                        {method.title}
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </CardGrid>

                    <Button
                        variant="outlined"
                        sx={{
                            mt: 4,
                            color: primaryBlue,
                            borderColor: primaryBlue,
                            borderRadius: '24px',
                            padding: '8px 32px',
                            fontSize: '1.1rem',
                            width: '200px',
                            '&:hover': {
                                borderColor: primaryBlue,
                                backgroundColor: 'rgba(31, 27, 158, 0.04)'
                            },
                            marginBottom: 10
                        }}
                        onClick={() => (window.location.href = `${getBaseUrl()}/Know-How`)}
                    >
                        Methoden<br />
                        kennenlernen
                    </Button>
                </ContentSection>

                <Box sx={{marginTop:"-200px"}}>
                <QuoteBox
                    text="„Die Zukunft gehört denen, die die Möglichkeiten erkennen, bevor sie offensichtlich werden.“"
                    maxWidth="1200px"
                    author={"Oscar Wilde"}
                />
                </Box>

                <Footer/>
            </ContentWrapper>
        </StyledContainer>
    );
};

export default Main;